import React, { useEffect, useMemo, useState } from 'react';
import Header from '../../components/layout/Header';
import { Button, Modal } from '../../components/common/Common';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../utils/api';
import {
  LifeBuoy, Send, Loader2, AlertCircle,
  Image as ImageIcon, X, Inbox, CheckCircle2, Clock,
  Eye, RefreshCw, Search, Star, RotateCcw, MessageSquare, Paperclip,
  Tag, Flame, Calendar, Hash, FileText, Hourglass
} from 'lucide-react';
import './HelpCenter.css';

const CATEGORIES = [
  'Leads Not Receiving',
  'Lead Data Incorrect',
  'Followup Reminder Issue',
  'Notifications Not Working',
  'Login / Access Issue',
  'Report / Data Incorrect',
  'App Crash / Bug',
  'Team / User Management',
  'Broker Management Issue',
  'Data Sync / Loading Issue',
  'Feature Request',
  'Other',
].map(v => ({ value: v, label: v }));

const normalizeStatus = (s) => String(s || '').toLowerCase().replace(/\s+/g, '_');

const STATUS_META = {
  open: { label: 'Open', icon: Inbox, color: 'info' },
  in_progress: { label: 'In Progress', icon: Clock, color: 'warning' },
  resolved: { label: 'Resolved', icon: CheckCircle2, color: 'success' },
  closed: { label: 'Closed', icon: CheckCircle2, color: 'muted' },
  reopened: { label: 'Reopened', icon: RotateCcw, color: 'info' },
};

const PRIORITY_COLOR = {
  low: 'muted', medium: 'info', high: 'warning', urgent: 'danger',
};

const HelpCenter = () => {
  const { showToast } = useToast();
  const { user, clientData, isAdmin } = useAuth();

  const clientCode = clientData?.clientCode || '';
  const currentUserId = user?.id || user?.u_id || null;

  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);

  // Submit form modal
  const [submitOpen, setSubmitOpen] = useState(false);
  const [category, setCategory] = useState('Other');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // View ticket modal
  const [viewTicket, setViewTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [reopening, setReopening] = useState(false);

  // Rating
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingSubmitting, setRatingSubmitting] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const loadTickets = async () => {
    if (!clientCode) return;
    setTicketsLoading(true);
    try {
      const params = new URLSearchParams({ client_code: clientCode, page: '1', limit: '100' });
      if (currentUserId) params.set('user_id', String(currentUserId));
      if (isAdmin) params.set('is_admin', 'true');
      const res = await apiFetch(`tickets-integration/my?${params.toString()}`);
      const list = res.tickets || res.data?.tickets || [];
      setTickets(Array.isArray(list) ? list : []);
    } catch {
      showToast('Failed to load tickets', 'error');
    }
    setTicketsLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadTickets(); }, [clientCode]);

  const resetSubmitForm = () => {
    setCategory('Other');
    setTitle('');
    setDescription('');
    setFiles([]);
  };

  const openSubmit = () => {
    resetSubmitForm();
    setSubmitOpen(true);
  };

  const closeSubmit = () => {
    if (submitting) return;
    setSubmitOpen(false);
  };

  const handleFilesChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const merged = [...files, ...picked].slice(0, 3);
    const oversize = merged.find(f => f.size > 5 * 1024 * 1024);
    if (oversize) {
      showToast('Each attachment must be under 5MB', 'error');
      return;
    }
    setFiles(merged);
    e.target.value = '';
  };

  const removeFile = (idx) => {
    setFiles(files.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 5) {
      showToast('Title must be at least 5 characters', 'error');
      return;
    }
    if (description.trim().length < 20) {
      showToast('Description must be at least 20 characters', 'error');
      return;
    }
    const uid = user?.id || user?.u_id;
    if (!uid) {
      showToast('User session missing', 'error');
      return;
    }
    if (!user?.email) {
      showToast('Please add your email in profile before raising a ticket', 'error');
      return;
    }
    const senderDisplayName = user.full_name || user.name || user.username || '';
    if (!senderDisplayName) {
      showToast('Please set your name/username in profile before raising a ticket', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('client_code', clientCode);
      fd.append('user_id', String(uid));
      fd.append('user_email', user.email);
      fd.append('client_name', senderDisplayName);
      fd.append('title', title.trim());
      fd.append('category', category);
      fd.append('description', description.trim());
      files.forEach(f => fd.append('attachments', f));

      const res = await apiFetch('tickets-integration', { method: 'POST', body: fd });
      if (res.success) {
        showToast(`Ticket created: ${res.ticket_number || ''}`, 'success');
        resetSubmitForm();
        setSubmitOpen(false);
        loadTickets();
      } else {
        showToast(res.message || 'Failed to submit ticket', 'error');
      }
    } catch {
      showToast('Connection failed', 'error');
    }
    setSubmitting(false);
  };

  const openViewTicket = async (t) => {
    setViewTicket(t);
    setReplies([]);
    setReplyText('');
    setRatingValue(t.rating || 0);
    setRatingComment(t.rating_comment || '');
    setViewLoading(true);
    try {
      const qp = new URLSearchParams();
      if (currentUserId) qp.set('user_id', String(currentUserId));
      if (isAdmin) qp.set('is_admin', 'true');
      const qs = qp.toString();
      const res = await apiFetch(`tickets-integration/${t.ticket_id}${qs ? '?' + qs : ''}`);
      if (res.ticket) {
        setViewTicket(res.ticket);
        setRatingValue(res.ticket.rating || 0);
        setRatingComment(res.ticket.rating_comment || '');
      }
      setReplies(Array.isArray(res.replies) ? res.replies : []);
    } catch {
      showToast('Failed to load ticket details', 'error');
    }
    setViewLoading(false);
  };

  const closeViewTicket = () => {
    if (replying || ratingSubmitting || reopening) return;
    setViewTicket(null);
    setReplies([]);
    setReplyText('');
    setRatingValue(0);
    setRatingComment('');
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      const senderDisplayName = user?.full_name || user?.name || user?.username || '';
      if (!senderDisplayName) {
        showToast('Please set your name/username in profile to reply', 'error');
        setReplying(false);
        return;
      }
      const res = await apiFetch(`tickets-integration/${viewTicket.ticket_id}/reply`, {
        method: 'POST',
        body: JSON.stringify({
          message: replyText.trim(),
          client_code: clientCode,
          client_name: senderDisplayName,
        }),
      });
      if (res.success) {
        setReplyText('');
        await openViewTicket(viewTicket);
        loadTickets();
        showToast('Reply sent', 'success');
      } else {
        showToast(res.message || 'Failed to send reply', 'error');
      }
    } catch {
      showToast('Connection failed', 'error');
    }
    setReplying(false);
  };

  const handleSubmitRating = async () => {
    if (!ratingValue || ratingValue < 1 || ratingValue > 5) {
      showToast('Please select 1-5 stars', 'error');
      return;
    }
    setRatingSubmitting(true);
    try {
      const res = await apiFetch(`tickets-integration/${viewTicket.ticket_id}/rating`, {
        method: 'POST',
        body: JSON.stringify({ rating: ratingValue, rating_comment: ratingComment.trim() }),
      });
      if (res.success) {
        showToast('Rating submitted', 'success');
        await openViewTicket(viewTicket);
        loadTickets();
      } else {
        showToast(res.message || 'Failed to submit rating', 'error');
      }
    } catch {
      showToast('Connection failed', 'error');
    }
    setRatingSubmitting(false);
  };

  const handleReopen = async () => {
    setReopening(true);
    try {
      const res = await apiFetch(`tickets-integration/${viewTicket.ticket_id}/reopen`, { method: 'PATCH' });
      if (res.success) {
        showToast('Ticket reopened', 'success');
        await openViewTicket(viewTicket);
        loadTickets();
      } else {
        showToast(res.message || 'Failed to reopen', 'error');
      }
    } catch {
      showToast('Connection failed', 'error');
    }
    setReopening(false);
  };

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
      });
    } catch { return d; }
  };

  const formatDateShort = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
      });
    } catch { return d; }
  };

  const categoryLabel = (val) =>
    CATEGORIES.find(c => c.value === val)?.label || val || 'Other';

  const stats = useMemo(() => {
    const norm = (t) => normalizeStatus(t.status);
    return {
      total: tickets.length,
      open: tickets.filter(t => norm(t) === 'open' || norm(t) === 'reopened').length,
      in_progress: tickets.filter(t => norm(t) === 'in_progress').length,
      resolved: tickets.filter(t => norm(t) === 'resolved' || norm(t) === 'closed').length,
    };
  }, [tickets]);

  const filteredTickets = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return tickets.filter(t => {
      const ns = normalizeStatus(t.status);
      if (statusFilter !== 'all') {
        if (statusFilter === 'open' && !(ns === 'open' || ns === 'reopened')) return false;
        else if (statusFilter === 'resolved' && !(ns === 'resolved' || ns === 'closed')) return false;
        else if (statusFilter === 'in_progress' && ns !== 'in_progress') return false;
      }
      if (q) {
        const hay = `${t.title || ''} ${t.ticket_number || ''} ${t.category || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tickets, searchQuery, statusFilter]);


  const ticketStatus = viewTicket ? normalizeStatus(viewTicket.status) : '';
  const canReply = viewTicket && ticketStatus !== 'closed';
  const canRate = viewTicket && ticketStatus === 'resolved';
  const canReopen = viewTicket && (ticketStatus === 'resolved' || ticketStatus === 'closed');
  const alreadyRated = viewTicket && Number(viewTicket.rating) > 0;

  return (
    <div>
      <Header title="Help Center" subtitle="Contact support and track your tickets" />
      <div className="page">
        <div className="hc-layout">

          {/* Hero with CTA */}
          <section className="hc-hero">
            <div className="hc-hero__icon">
              <LifeBuoy size={28} />
            </div>
            <div className="hc-hero__content">
              <h2 className="hc-hero__title">How can we help you today?</h2>
              <p className="hc-hero__sub">Raise a support ticket and our team will get back to you. Track all your requests below.</p>
            </div>
            <div className="hc-hero__cta">
              <button type="button" className="hc-cta-btn" onClick={openSubmit}>
                Submit Ticket
              </button>
            </div>
          </section>

          {/* Stats */}
          <div className="hc-totals">
            <button
              type="button"
              className={`hc-total-card ${statusFilter === 'all' ? 'hc-total-card--active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              <div className="hc-total-card__head">
                <span className="hc-total-card__icon hc-total-card__icon--total"><Inbox size={15} /></span>
                <span className="hc-total-label">Total Tickets</span>
              </div>
              <span className="hc-total-value">{stats.total}</span>
            </button>
            <button
              type="button"
              className={`hc-total-card ${statusFilter === 'open' ? 'hc-total-card--active' : ''}`}
              onClick={() => setStatusFilter('open')}
            >
              <div className="hc-total-card__head">
                <span className="hc-total-card__icon hc-total-card__icon--open"><AlertCircle size={15} /></span>
                <span className="hc-total-label">Open</span>
              </div>
              <span className="hc-total-value">{stats.open}</span>
            </button>
            <button
              type="button"
              className={`hc-total-card ${statusFilter === 'in_progress' ? 'hc-total-card--active' : ''}`}
              onClick={() => setStatusFilter('in_progress')}
            >
              <div className="hc-total-card__head">
                <span className="hc-total-card__icon hc-total-card__icon--progress"><Clock size={15} /></span>
                <span className="hc-total-label">In Progress</span>
              </div>
              <span className="hc-total-value">{stats.in_progress}</span>
            </button>
            <button
              type="button"
              className={`hc-total-card ${statusFilter === 'resolved' ? 'hc-total-card--active' : ''}`}
              onClick={() => setStatusFilter('resolved')}
            >
              <div className="hc-total-card__head">
                <span className="hc-total-card__icon hc-total-card__icon--resolved"><CheckCircle2 size={15} /></span>
                <span className="hc-total-label">Resolved</span>
              </div>
              <span className="hc-total-value">{stats.resolved}</span>
            </button>
          </div>

          {/* My Tickets */}
          <section className="hc-card">
            <div className="hc-card__head">
              <Inbox size={16} className="hc-card__head-icon" />
              <h3 className="hc-card__title">My Tickets</h3>
              <button
                type="button"
                className="hc-card__head-action"
                onClick={loadTickets}
                disabled={ticketsLoading}
                title="Refresh"
              >
                <RefreshCw size={13} className={ticketsLoading ? 'spin' : ''} />
                Refresh
              </button>
            </div>

            {/* Filter bar */}
            <div className="hc-filterbar">
              <div className="hc-search">
                <Search size={14} />
                <input
                  type="text"
                  placeholder="Search by title, ticket number or category…"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button type="button" className="hc-search__clear" onClick={() => setSearchQuery('')} title="Clear">
                    <X size={13} />
                  </button>
                )}
              </div>
              <div className="hc-filter-group">
                <label htmlFor="hc-status">Status:</label>
                <select
                  id="hc-status"
                  className="hc-select"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved / Closed</option>
                </select>
              </div>
            </div>

            {ticketsLoading ? (
              <div className="hc-state">
                <Loader2 size={18} className="spin" />
                <span>Loading tickets…</span>
              </div>
            ) : tickets.length === 0 ? (
              <div className="hc-state hc-state--empty">
                <AlertCircle size={20} />
                <span>You haven't submitted any tickets yet.</span>
                <Button variant="gold" size="sm" onClick={openSubmit}>
                  <Send size={13} /> Submit Your First Ticket
                </Button>
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="hc-state hc-state--empty">
                <AlertCircle size={20} />
                <span>No tickets match your filters.</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
                >
                  Clear filters
                </Button>
              </div>
            ) : (
              <div className="hc-table-wrap">
                <table className="hc-table">
                  <thead>
                    <tr>
                      <th className="hc-th-id">Ticket #</th>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th className="hc-th-action">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTickets.map(t => {
                      const ns = normalizeStatus(t.status);
                      const meta = STATUS_META[ns] || STATUS_META.open;
                      const StatusIcon = meta.icon;
                      const pColor = PRIORITY_COLOR[String(t.priority || '').toLowerCase()] || 'muted';
                      return (
                        <tr key={t.ticket_id} className="hc-tr">
                          <td className="hc-td-id">{t.ticket_number || `#${t.ticket_id}`}</td>
                          <td className="hc-td-subject" data-label="Title">
                            <span className="hc-td-subject__text">
                              {t.title}
                              {t.has_unread_admin_reply ? <span className="hc-unread-dot" title="New reply" /> : null}
                            </span>
                          </td>
                          <td data-label="Category">
                            <span className={`hc-cat-tag hc-cat-tag--${String(t.category || 'other').toLowerCase()}`}>
                              {categoryLabel(t.category)}
                            </span>
                          </td>
                          <td data-label="Priority">
                            {t.priority ? (
                              <span className={`hc-priority hc-priority--${pColor}`}>{t.priority}</span>
                            ) : <span className="hc-muted">—</span>}
                          </td>
                          <td data-label="Status">
                            <span className={`hc-status hc-status--${meta.color}`}>
                              <StatusIcon size={12} />
                              {meta.label}
                            </span>
                          </td>
                          <td className="hc-td-date" data-label="Created">{formatDateShort(t.created_at)}</td>
                          <td className="hc-td-action">
                            <button
                              type="button"
                              className="hc-action-btn"
                              onClick={() => openViewTicket(t)}
                              title="View details"
                            >
                              <Eye size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

        </div>
      </div>

      {/* Submit Ticket Modal */}
      <Modal isOpen={submitOpen} onClose={closeSubmit} title="Submit a Support Ticket" size="md">
        <form className="hc-form" onSubmit={handleSubmit}>
          <div className="hc-field">
            <label htmlFor="hc-category">Category</label>
            <select
              id="hc-category"
              className="hc-input"
              value={category}
              onChange={e => setCategory(e.target.value)}
              disabled={submitting}
              required
            >
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div className="hc-field">
            <label htmlFor="hc-title">Title <span className="hc-field__opt">(min 5 chars)</span></label>
            <input
              id="hc-title"
              type="text"
              className="hc-input"
              placeholder="A short summary of the issue"
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={200}
              disabled={submitting}
              required
            />
          </div>

          <div className="hc-field">
            <label htmlFor="hc-desc">Description <span className="hc-field__opt">(min 20 chars)</span></label>
            <textarea
              id="hc-desc"
              className="hc-textarea"
              rows={5}
              placeholder="Tell us what happened, what you expected, and any steps to reproduce."
              value={description}
              onChange={e => setDescription(e.target.value)}
              disabled={submitting}
              required
            />
          </div>

          <div className="hc-field">
            <label>Attachments <span className="hc-field__opt">(optional, up to 3 files, 5MB each)</span></label>
            <div className="hc-attach-list">
              {files.map((f, i) => (
                <div key={i} className="hc-attach-chip">
                  <Paperclip size={12} />
                  <span className="hc-attach-chip__name">{f.name}</span>
                  <button
                    type="button"
                    className="hc-attach-chip__remove"
                    onClick={() => removeFile(i)}
                    disabled={submitting}
                    title="Remove"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              {files.length < 3 && (
                <label className="hc-attach-drop hc-attach-drop--compact">
                  <ImageIcon size={16} />
                  <span>Add file{files.length > 0 ? ` (${3 - files.length} left)` : ''}</span>
                  <input
                    type="file"
                    onChange={handleFilesChange}
                    hidden
                    disabled={submitting}
                    multiple
                  />
                </label>
              )}
            </div>
          </div>

          <div className="hc-form__actions">
            <Button variant="outline" type="button" onClick={closeSubmit} disabled={submitting}>
              Cancel
            </Button>
            <button type="submit" className="hc-cta-btn" disabled={submitting}>
              {submitting ? <><Loader2 size={14} className="spin" /> Submitting...</> : 'Submit Ticket'}
            </button>
          </div>
        </form>
      </Modal>

      {/* View Ticket Modal */}
      <Modal
        isOpen={!!viewTicket}
        onClose={closeViewTicket}
        title=""
        size="lg"
      >
        {viewTicket && (
          <div className="hc-view">
            <div className="hc-view__head">
              <div className="hc-view__head-main">
                <span className="hc-view__num">
                  <Hash size={11} />
                  {viewTicket.ticket_number || `TKT-${viewTicket.ticket_id}`}
                </span>
                <h3 className="hc-view__subject">{viewTicket.title}</h3>
              </div>
              <span className={`hc-status hc-status--${(STATUS_META[ticketStatus] || STATUS_META.open).color} hc-status--lg`}>
                {React.createElement((STATUS_META[ticketStatus] || STATUS_META.open).icon, { size: 13 })}
                {(STATUS_META[ticketStatus] || STATUS_META.open).label}
              </span>
            </div>

            <div className="hc-view__meta">
              <div className="hc-view__meta-item">
                <span className="hc-view__meta-label"><Tag size={11} /> Category</span>
                <span className="hc-cat-tag">{categoryLabel(viewTicket.category)}</span>
              </div>
              {viewTicket.priority && (
                <div className="hc-view__meta-item">
                  <span className="hc-view__meta-label"><Flame size={11} /> Priority</span>
                  <span className={`hc-priority hc-priority--${PRIORITY_COLOR[String(viewTicket.priority).toLowerCase()] || 'muted'}`}>
                    {viewTicket.priority}
                  </span>
                </div>
              )}
              <div className="hc-view__meta-item">
                <span className="hc-view__meta-label"><Calendar size={11} /> Created</span>
                <span className="hc-view__meta-value">{formatDate(viewTicket.created_at)}</span>
              </div>
              {viewTicket.sla_due_at && (
                <div className="hc-view__meta-item">
                  <span className="hc-view__meta-label"><Hourglass size={11} /> SLA Due</span>
                  <span className="hc-view__meta-value">{formatDate(viewTicket.sla_due_at)}</span>
                </div>
              )}
            </div>

            <div className="hc-view__section">
              <span className="hc-view__meta-label hc-view__section-label">
                <FileText size={12} /> Description
              </span>
              <div className="hc-view__desc-card">
                <p className="hc-view__desc">{viewTicket.description}</p>
              </div>
            </div>

            {/* Replies thread */}
            <div className="hc-view__section">
              <span className="hc-view__meta-label hc-view__section-label">
                <MessageSquare size={12} /> Conversation
                <span className="hc-count-badge">{replies.length}</span>
              </span>
              {viewLoading ? (
                <div className="hc-state">
                  <Loader2 size={16} className="spin" />
                  <span>Loading…</span>
                </div>
              ) : replies.length === 0 ? (
                <div className="hc-thread-empty">No replies yet.</div>
              ) : (
                <div className="hc-thread">
                  {(() => {
                    const myDisplayName = user?.full_name || user?.name || user?.username || '';
                    return replies.map(r => {
                      const isAdmin = r.sender_type === 'admin';
                      const isSelf = !isAdmin && r.sender_name && r.sender_name === myDisplayName;
                      const senderName = isSelf ? 'You' : (r.sender_name || (isAdmin ? 'Support' : 'Client'));
                      const initials = String(senderName).trim().split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || (isAdmin ? 'S' : 'Y');
                      return (
                        <div
                          key={r.reply_id}
                          className={`hc-msg hc-msg--${isAdmin ? 'admin' : 'client'}`}
                        >
                          <span className="hc-msg__avatar">{initials}</span>
                          <div className="hc-msg__bubble">
                            <div className="hc-msg__head">
                              <span className="hc-msg__sender">{senderName}</span>
                              <span className="hc-msg__time">{formatDate(r.created_at)}</span>
                            </div>
                            <div className="hc-msg__body">{r.message}</div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>

            {/* Reply box */}
            {canReply && (
              <form className="hc-reply" onSubmit={handleReply}>
                <textarea
                  className="hc-textarea"
                  rows={3}
                  placeholder="Type your reply…"
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  disabled={replying}
                />
                <div className="hc-reply__actions">
                  <button type="submit" className="hc-cta-btn" disabled={replying || !replyText.trim()}>
                    {replying ? <><Loader2 size={14} className="spin" /> Sending…</> : 'Send Reply'}
                  </button>
                </div>
              </form>
            )}

            {/* Rating block (Resolved only) */}
            {canRate && (
              <div className="hc-rating">
                <span className="hc-view__meta-label hc-view__section-label">
                  <Star size={12} />
                  {alreadyRated ? 'Your Rating' : 'Rate this resolution'}
                </span>
                <div className="hc-rating__stars">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      type="button"
                      className={`hc-star ${n <= ratingValue ? 'hc-star--active' : ''}`}
                      onClick={() => !alreadyRated && setRatingValue(n)}
                      disabled={alreadyRated || ratingSubmitting}
                      title={`${n} star${n > 1 ? 's' : ''}`}
                    >
                      <Star size={20} fill={n <= ratingValue ? 'currentColor' : 'none'} />
                    </button>
                  ))}
                </div>
                {!alreadyRated && (
                  <>
                    <textarea
                      className="hc-textarea"
                      rows={2}
                      placeholder="Optional comment about the support…"
                      value={ratingComment}
                      onChange={e => setRatingComment(e.target.value)}
                      disabled={ratingSubmitting}
                    />
                    <div className="hc-rating__actions">
                      <button type="button" className="hc-cta-btn" onClick={handleSubmitRating} disabled={ratingSubmitting || !ratingValue}>
                        {ratingSubmitting ? <><Loader2 size={14} className="spin" /> Submitting…</> : 'Submit Rating'}
                      </button>
                    </div>
                  </>
                )}
                {alreadyRated && viewTicket.rating_comment && (
                  <p className="hc-rating__comment">"{viewTicket.rating_comment}"</p>
                )}
              </div>
            )}

            <div className="hc-view__actions">
              {canReopen && (
                <Button variant="outline" type="button" onClick={handleReopen} disabled={reopening}>
                  {reopening ? <><Loader2 size={14} className="spin" /> Reopening…</> : <><RotateCcw size={13} /> Reopen Ticket</>}
                </Button>
              )}
              <Button variant="outline" onClick={closeViewTicket}>Close</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default HelpCenter;
